import { useEffect, useState } from 'react';

import { getDelegation } from '@/authz/delegation';
import { showToast } from '@/components/reusable/toast';
import { useGetCandidateElectionMutation, useLazyGetElectionByIdQuery } from '@/store/api';

import type { IInputSearchResult } from '../components/reusable/input-search/utils';
import {
	COMPANY_ORGANIZATION,
	DEFAULT_ORGANIZATION,
	DEFAULT_ORGANIZATION_SELECTED_ELECTION,
	getUserData,
	type IORGANIZATION,
	type IORGANIZATION_SELECTED_ELECTION,
	ISADMINLOGGED,
	ISSUPERADMIN,
} from '../constants/global';
import type { IActions } from '../interface/IAction';
import type { IGetPartyByIDState } from '../interface/state/IPartyState';
import { useModal } from './modal';

const useOrganization = (actions: IActions | undefined): IORGANIZATION => {
	const modalProps = useModal();

	const [organization, setOrganization] = useState<IGetPartyByIDState>(DEFAULT_ORGANIZATION);

	const [selectedElection, setSelectedElection] = useState<IORGANIZATION_SELECTED_ELECTION>(
		DEFAULT_ORGANIZATION_SELECTED_ELECTION
	);

	// A team member's token carries `Delegate Support` only; `OwnerRole` says whose account they work
	// in. These two flags decide SCOPE (which election, which picker), so a team member takes the
	// owner's — permissions never read them.
	const delegation = getDelegation();

	const getIsCandidate = () => {
		if (delegation.isTeamMember) return delegation.ownerRole === 'Candidate';
		const userRole = getUserData().user?.role;

		const CANDIDATE_ROLE = 'Candidate';

		return Boolean(typeof userRole === 'string' ? userRole === CANDIDATE_ROLE : userRole?.includes(CANDIDATE_ROLE));
	};

	const getIsObserverAdmin = () => {
		if (delegation.isTeamMember) return delegation.ownerRole === 'Observer Admin';
		const userRole = getUserData().user?.role;

		const OBSERVER_ADMIN_ROLE = 'Observer Admin';
		return Boolean(typeof userRole === 'string' ? userRole === OBSERVER_ADMIN_ROLE : userRole?.includes(OBSERVER_ADMIN_ROLE));
	};

	const isCandidate = getIsCandidate();
	const isObserverAdmin = getIsObserverAdmin();
	// An observer scopes to an election on Manage Observers (partner › election), never through
	// the global picker. Super Admin wins if both roles are ever held — roles are a union.
	const isObserver = isObserverAdmin && !ISSUPERADMIN;

	const setPartyOrganization = (partyId: string) => {
		actions?.get_PartyByID({
			id: partyId,
			onSuccess: (res) => {
				const organizationData = res.data;

				if (!organizationData) return;

				setOrganization(organizationData);
			},
		});
	};

	const setCompanyOrganization = () => {
		setOrganization(COMPANY_ORGANIZATION);
	};

	const getStoredElection = () => {
		const data = localStorage.getItem('election');
		if (!data) return;
		const storedElection = JSON.parse(data) as IORGANIZATION_SELECTED_ELECTION;

		return storedElection;
	};

	const [getCandidateElection] = useGetCandidateElectionMutation();
	const setCandidateElection = () => {
		getCandidateElection({
			id: getUserData().user?.UserId ?? '',
		})
			.unwrap()
			.then((res) => {
				const electionData = res.data.election;

				storeElectionSelection({ value: electionData.id, label: electionData.name });
			})
			.catch(() => {
				showToast('error', 'Failed to fetch election for candidate');
			});
	};

	const [getElectionById] = useLazyGetElectionByIdQuery();
	/**
	 * A Candidate's team member is locked to the `ElectionId` claim. The candidate lookup above keys
	 * on the CALLER's user id, which for a team member is not a candidate, so it would only toast
	 * "Failed to fetch election for candidate". The claim is authoritative even over a stored
	 * selection; the lookup by id only supplies the name.
	 */
	const lockToClaimedElection = (electionId: number, stored?: IORGANIZATION_SELECTED_ELECTION) => {
		const storedName = stored && String(stored.electionId) === String(electionId) ? stored.electionName : '';
		storeElectionSelection({ value: String(electionId), label: storedName || `Election ${electionId}` });
		if (storedName) return;
		getElectionById({ id: String(electionId) })
			.unwrap()
			.then((res) => {
				const name = res?.data?.name?.trim();
				if (name) storeElectionSelection({ value: String(electionId), label: name });
			})
			.catch(() => {
				// The id is already locked; only the label is missing. No toast — nothing failed for the user.
			});
	};

	const selectElection = () => {
		const storedElection = getStoredElection();

		if (delegation.isTeamMember && isCandidate && delegation.electionId) {
			lockToClaimedElection(delegation.electionId, storedElection);
			return;
		}

		if (storedElection?.electionId) {
			setSelectedElection(storedElection);
			return;
		}

		if (ISSUPERADMIN) {
			return;
		}

		if (isObserverAdmin) {
			return;
		}

		// A team member with no `ElectionId` claim falls through to the picker: the candidate lookup
		// would key on their own id and fail.
		if (isCandidate && !delegation.isTeamMember) {
			setCandidateElection();
			return;
		}

		modalProps.handleOpenModal('Election');
	};

	const storeElectionSelection = (election: IInputSearchResult, stateId?: number) => {
		const selectedElectionProps = { electionId: election.value, electionName: election.label, stateId };
		localStorage.setItem('election', JSON.stringify(selectedElectionProps));
		setSelectedElection(selectedElectionProps);
	};

	function parsePartyName(input: string) {
		const match = /^(.+?)\s*\(([^)]+)\)$/.exec(input);
		if (!match) {
			return { longName: input, shortName: '' };
		}

		const [, longName, shortName] = match;
		return { longName: longName.trim(), shortName: shortName.trim() };
	}

	const storePartySelection = (party: IInputSearchResult) => {
		const { longName, shortName } = parsePartyName(party.label);
		const selectedPartyProps = { partyId: party.value, longName, shortName };
		localStorage.setItem('party', JSON.stringify(selectedPartyProps));
	};

	const setupOrganization = (partyId: string) => {
		if (ISSUPERADMIN) {
			setCompanyOrganization();
			return;
		}
		// An observer belongs to a development partner, not a party: the token carries PartyId "0",
		// and GET /Party/0 errors. "0" is truthy, so the caller's `!partyId` check never caught it —
		// only a real positive id is looked up.
		const id = Number(partyId);
		if (isObserver || !Number.isFinite(id) || id <= 0) return;
		setPartyOrganization(partyId);
	};

	const cancelSelection = () => {
		setSelectedElection(DEFAULT_ORGANIZATION_SELECTED_ELECTION);
		localStorage.removeItem('election');
		localStorage.removeItem('party');
		window.open('/admin/overview', '_self');
	};

	const onSelectElection = () => {
		if (isObserver) return;
		modalProps.handleOpenModal('Election');
	};

	useEffect(() => {
		if (ISADMINLOGGED()) {
			const partyId = getUserData().user?.PartyId;

			if (!partyId) return;

			setupOrganization(partyId);

			selectElection();
		}
	}, []);

	return {
		modalProps,
		selectedElection,
		...organization,
		storeElectionSelection,
		cancelSelection,
		onSelectElection,
		storePartySelection,
		isCandidate,
		isObserver,
	};
};

export default useOrganization;
